import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./stats-box.module.css";
import { useLoadStats } from "./useGetAllStats";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/free-solid-svg-icons";
import { useFrameOwners, getOwnerInfo } from "../art_of_the_day/useFrameOwners";
import { FrameOwnerBadge } from "../art_of_the_day/FrameOwnerBadge";
import GameBoard from "../../features/game/components/GameBoard";
import { FrameArea, FrameBracket, FrameLegend, FrameNote, frameRowFill } from "../../features/game/components/FrameMarks";
import { useCurrentGame } from "../../features/game/hooks/useCurrentGame";
import {
  FRAME_YEAR,
  PROVISIONAL_NOTE,
  aotdPostsOrder,
  aotdRowFrameKind,
  bracketPieces,
  framesProvisional,
} from "../../features/game/utils/frames";
import type { FrameKind } from "../../features/game/utils/frames";
import { Emoji } from "../../ui";
import { resolveUserEmoji } from "../../utils/userHandle";

interface Author {
  _id: string;
  userEmoji: string;
  postsCount: number;
  hasToday?: number;
}

interface Stats {
  aotdAuthors: Array<Author>;
}

const TodayIcon = ({ size = 14, className }: { size?: number; className?: string }) => (
  <svg
    viewBox="0 0 64 64"
    width={size}
    height={size}
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
    aria-hidden
  >
    <circle cx="32" cy="32" r="14" stroke="currentColor" strokeWidth="4" />
    <circle cx="32" cy="32" r="5" fill="currentColor" />
  </svg>
);

// turtle from turtle_2.svg, fill uses currentColor for parent color override
const TurtleIcon = ({ size = 14, className }: { size?: number; className?: string }) => (
  <svg
    viewBox="0 0 512 512"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
    style={{ display: "block" }}
  >
    <path d="M230.44,89.6c67.15,0,126.62,42.61,148.47,105.62l1.94,5.96l1.11,3.69l59.67,0.01c37.42,0,68.03,29.21,70.24,66.08l0.13,4.29v25.58c0,23.49-18.09,42.76-41.11,44.63l-3.67,0.15l-41.62-0.02l6.18,14.96c8.93,21.66-0.38,46.29-20.82,56.84l-3.51,1.63c-4.06,1.67-8.34,2.74-12.69,3.17l-4.38,0.21h-45.73c-16.01,0-30.7-8.53-38.67-22.19l-1.85-3.52l-13.79-29.25l1.38-0.18c-35.85,4.76-72.12,5.33-108.07,1.69l-12.72-1.46l-13.7,29.1c-7.39,15.7-23.17,25.71-40.52,25.71H70.51c-5.85,0-11.64-1.14-17.05-3.37c-22.87-9.42-33.78-35.59-24.36-58.46l11.09-26.89C16.11,319.04,0,292.62,0,262.44c0-9.71,7.22-17.74,16.59-19.02l2.6-0.17l48.14-0.01l12.73-42.04C100.12,134.93,161.2,89.6,230.44,89.6z M403.93,337.75c-19.75,7.93-39.9,14.46-60.31,19.58l-14.05,3.29l9.27,19.72l2.99,3.03l1.35,0.48l1.45,0.17h45.73l2.44-0.48c2.72-1.12,4.25-3.91,3.91-6.69l-0.44-1.66l-12.22-29.59l-1.11,0.01l10.53-3.79L403.93,337.75z M57.01,337.75l10.01,3.89l9.9,3.61L64.6,375.08c-1.34,3.27,0.21,7.01,3.48,8.35l2.44,0.48h46.19l1.45-0.17l4.34-3.51l9.21-19.54l1.13,0.25C107.11,355.42,81.74,347.69,57.01,337.75z M51.83,294.37l3.88-12.78l-12.97,0.03l0.64,1.34C45.57,287.2,48.44,291.05,51.83,294.37z M393.58,243.26l19.37,63.94l54.26,0.03c3.03,0,5.57-2.1,6.23-4.93l0.17-1.47v-25.58c0-16.56-12.59-30.18-28.71-31.82l-3.27-0.16L393.58,243.26z" />
  </svg>
);

const Title = styled.div`
  font-size: 1.1rem;
  color: #222;
  text-align: center;
  font-weight: 500;
  padding: 5px;
  padding-bottom: 2px;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 700px) {
    padding: 8px;
  }
`;

const TableScrollWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const ShowMoreButton = styled.button`
  /* display: flex; */
  width: 200px;
  height: 30px;
  color: rgba(228, 228, 228, 0.952);
  border: 2px solid  rgba(228, 228, 228, 0.041);
  background-color: #2424247f;
  cursor: pointer;
  text-align: center;
  display: inline-block;
  font-size: 16px;
  transition: transform 0.4s, box-shadow 0.4s;
  margin-top: -16px;
  /* overflow: hidden; */
  box-shadow: 4px 4px rgba(0, 0, 0, 0.156);
  /* z-index: 0; */
  &:hover {
    /* transform: translateY(3px); */
    transform: var(--hm-press-translate);
    box-shadow: 3px 3px rgba(0, 0, 0, 0.5);
    background-color: #3333338f;
  }
  &:active {
    transform: translateY(0);
    box-shadow: 3px 3px rgba(0, 0, 0, 0.3);
    background-color: #2a2a2ad9;
  }

  @media (max-width: 700px) {
    width: 100%;
    max-width: 100%;
  }
`;

const YearSelector = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 10px;
`;

const YearDropdown = styled.div`
  position: absolute;
  right: 0;
  top: 25px;
  background-color: #ffffff;
  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
  z-index: 100;
  min-width: 120px;
  border-radius: 4px;
`;

const YearOption = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  &:hover {
    background-color: #f1f1f1;
  }
`;

const YearDisplay = styled.span`
  cursor: pointer;
  position: relative;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FastestBadge = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: default;

  &:hover > span {
    visibility: visible;
    opacity: 1;
  }
`;

const GoldBolt = styled(FastestBadge)`
  color: #d4a017;
  filter: drop-shadow(0 0 4px #fbbf24) drop-shadow(0 0 8px #f59e0b80);
  z-index: 4;
`;

const PurpleBolt = styled(FastestBadge)`
  color: #7c3aed;
  z-index: 3;
`;

const TurtleBadge = styled(FastestBadge)`
  z-index: 2;
`;

const GoldTurtle = styled(TurtleBadge)`
  color: #d4a017;
  filter: drop-shadow(0 0 4px #fbbf24) drop-shadow(0 0 8px #f59e0b80);
`;

const PurpleTurtle = styled(TurtleBadge)`
  color: #7c3aed;
  z-index: 1;
`;

const BadgeSubGroup = styled.span`
  display: inline-flex;
  align-items: center;

  & > *:not(:first-child) {
    margin-left: -8px;
  }
`;

// A little flap that "folds out" from the right edge of the table row it
// belongs to. Mirrors the table's own cell styling (3px black border,
// translucent fill, offset shadow) so it reads as part of the table.
const BadgeTab = styled.span`
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  margin-left: 3px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  background-color: #ffffff30;
  border: var(--hm-border);
  border-left: none;
  box-shadow: var(--hm-shadow-sm);
  white-space: nowrap;
  line-height: 1;
  z-index: 5;

  /* on mobile, keep the flap chrome (white fill, black border, offset shadow)
     but sit inline next to the posts count instead of overflowing off the
     right edge of the table. restore the left border since the pill is no
     longer "attached" to the table edge. */
  @media (max-width: 700px) {
    position: static;
    transform: none;
    margin-left: 6px;
    border-left: var(--hm-border);
    vertical-align: middle;
  }
`;

// Anchors the frame bracket, drawn just left of the table.
const RankCell = styled.td`
  position: relative;
`;

// The 2026 prize: the top 3 each get an AOTD IRL, and the game's frame goes to
// its best player outside that top 3.
const FRAME_LABELS: Record<FrameKind, string> = {
  leaderboard: "will get an AOTD IRL",
  game: "will get the game's AOTD IRL",
};

const PostsCell = styled.td`
  position: relative;
  white-space: nowrap;
`;

const BadgeGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const TodayBadge = styled(FastestBadge)`
  color: #6366f1;
  z-index: 2;
`;

// Anchored to the badge's right edge so it always opens leftward into the
// page — the badges live on the table's far-right flap, so a centred tooltip
// would spill off-screen.
const Tooltip = styled.span`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: calc(100% + 9px);
  right: -4px;
  background-color: #1f1f1f;
  color: #fff;
  font-size: 0.72rem;
  line-height: 1.35;
  padding: 6px 10px;
  border-radius: 7px;
  max-width: 230px;
  width: max-content;
  white-space: normal;
  text-align: left;
  box-shadow: 0 5px 16px rgba(0, 0, 0, 0.4);
  z-index: 200;
  transition: opacity 0.18s ease;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 10px;
    border-width: 5px;
    border-style: solid;
    border-color: #1f1f1f transparent transparent transparent;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 14px;
  margin-top: 6px;
  margin-bottom: 12px;
  font-size: 0.72rem;
  color: #1a1a1a;
  align-items: center;
  font-weight: 500;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

function formatOffsetMs(ms: number): string {
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `+${totalSeconds.toFixed(2)}s`;
  const rounded = Math.round(totalSeconds);
  const totalMinutes = Math.floor(rounded / 60);
  const s = rounded % 60;
  if (totalMinutes >= 100) {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m > 0 ? `+${h}h ${m}m ${s}s` : `+${h}h ${s}s`;
  }
  return `+${totalMinutes}m ${s}s`;
}

const AotdAuthorsLeaderboard = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(currentYear);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showAllAuthors, setShowAllAuthors] = useState(false);
  const { leaderboard } = useLoadStats(selectedYear);

  const authorUsernames = useMemo(
    () => (leaderboard?.aotdAuthors ?? []).map((a) => String(a._id)).filter(Boolean),
    [leaderboard]
  );
  const { owners } = useFrameOwners(authorUsernames);
  const { frames } = useCurrentGame();
  // The game board belongs on the home page only; this leaderboard is also on
  // the admin stats page (userStats.tsx), which leaves it out.
  const showGameBoard = useLocation().pathname === "/";
  const gameBoard = showGameBoard ? <GameBoard /> : null;

  if (!leaderboard) {
    return (
      <>
        {gameBoard}
        <div>Loading...</div>
      </>
    );
  }

  // Frame marks only for the prize year, and only once the loaded data is that
  // year (useLoadStats keeps the previous year's data until the new one lands).
  // The top 3 comes from the backend's frames when it sent them, so this board
  // and the game board agree on a tie at 3rd.
  const frameYear = frames?.year ?? FRAME_YEAR;
  const showFrames = selectedYear === frameYear && leaderboard.fastestRecordYear?.year === frameYear;
  const byPosts = aotdPostsOrder(showFrames ? frames : null);

  const displayedAuthors = showAllAuthors
    ? leaderboard.aotdAuthors
    : leaderboard.aotdAuthors.sort(byPosts).slice(0, 5); // Display only the top 5 authors

  const frameKinds: Array<FrameKind | null> = displayedAuthors
    .sort(byPosts)
    .map((author, index) => (showFrames ? aotdRowFrameKind(String(author._id), index, frames) : null));
  const framePieces = bracketPieces(frameKinds, FRAME_LABELS);
  const hasFrames = frameKinds.some(Boolean);

  // generate years from 2023 to current year
  const years = Array.from({ length: currentYear - 2022 }, (_, i) => currentYear - i);

  return (
    <>
    {gameBoard}
    <TableContainer>
      <Title>
        AOTD Authors Leaderboard
        <YearSelector>
          <YearDisplay onClick={() => setShowYearDropdown(!showYearDropdown)}>
            <span className="font-black"> {selectedYear || 'All Years'} </span>
            <i className={`fa fa-chevron-${showYearDropdown ? 'up' : 'down'}`} style={{
              fontSize: '0.8rem',
              marginBottom: '3px',
              verticalAlign: 'middle',
              display: 'inline-flex',
              alignItems: 'center'
            }}></i>
          </YearDisplay>
          {showYearDropdown && (
            <YearDropdown>
              <YearOption onClick={() => {
                setSelectedYear(null);
                setShowYearDropdown(false);
              }}>All Years</YearOption>
              {years.map(year => (
                <YearOption key={year} onClick={() => {
                  setSelectedYear(year);
                  setShowYearDropdown(false);
                }}>{year}</YearOption>
              ))}
            </YearDropdown>
          )}
        </YearSelector>
      </Title>
      <TableScrollWrap>
      <FrameArea $marked={hasFrames}>
      <table className={styles.table} style={{ overflow: "visible" }}>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Author</th>
            <th>Posts</th>
          </tr>
        </thead>
        <tbody>
          {displayedAuthors
            .sort(byPosts)
            .map((author, index) => {
              const isAllTimeFastest = leaderboard.fastestRecordAllTime?.username === author._id;
              const isYearFastest = leaderboard.fastestRecordYear?.username === author._id;
              const isAllTimeSlowest = leaderboard.slowestRecordAllTime?.username === author._id;
              const isYearSlowest = leaderboard.slowestRecordYear?.username === author._id;
              const hasBadges = isAllTimeFastest || isYearFastest || isAllTimeSlowest || isYearSlowest;
              const isTodayAuthor = author.hasToday === 1;
              const ownerInfo = getOwnerInfo(owners, String(author._id));
              const showBadgeTab = hasBadges || isTodayAuthor || ownerInfo;
              const framePiece = framePieces[index];
              return (
                <tr
                  key={index}
                  data-frame={framePiece?.kind}
                  style={{ backgroundColor: frameRowFill(framePiece?.kind) }}
                >
                  <RankCell>
                    {index + 1}
                    {framePiece && <FrameBracket piece={framePiece} srText={FRAME_LABELS[framePiece.kind]} />}
                  </RankCell>
                  <td>
                    <Emoji>{resolveUserEmoji(author.userEmoji)}</Emoji>.{author._id}
                  </td>
                  <PostsCell>
                    {author.postsCount}
                    {showBadgeTab && (
                      <BadgeTab>
                        {isTodayAuthor && (
                          <TodayBadge>
                            <TodayIcon size={14} />
                            <Tooltip>today&apos;s AOTD author</Tooltip>
                          </TodayBadge>
                        )}
                        {hasBadges && (
                          <BadgeGroup>
                            {(isAllTimeFastest || isYearFastest) && (
                              <BadgeSubGroup>
                                {isAllTimeFastest && leaderboard.fastestRecordAllTime && (
                                  <GoldBolt>
                                    <FontAwesomeIcon icon={faBolt} size="sm" />
                                    <Tooltip>
                                      Fastest AOTD ever: {formatOffsetMs(leaderboard.fastestRecordAllTime.offsetMs)} on {new Date(leaderboard.fastestRecordAllTime.date).toLocaleDateString()}
                                    </Tooltip>
                                  </GoldBolt>
                                )}
                                {isYearFastest && leaderboard.fastestRecordYear && (
                                  <PurpleBolt>
                                    <FontAwesomeIcon icon={faBolt} size="sm" />
                                    <Tooltip>
                                      Fastest AOTD {leaderboard.fastestRecordYear.year}: {formatOffsetMs(leaderboard.fastestRecordYear.offsetMs)} on {new Date(leaderboard.fastestRecordYear.date).toLocaleDateString()}
                                    </Tooltip>
                                  </PurpleBolt>
                                )}
                              </BadgeSubGroup>
                            )}
                            {(isAllTimeSlowest || isYearSlowest) && (
                              <BadgeSubGroup>
                                {isAllTimeSlowest && leaderboard.slowestRecordAllTime && (
                                  <GoldTurtle>
                                    <TurtleIcon size={14} />
                                    <Tooltip>
                                      Slowest AOTD ever: {formatOffsetMs(leaderboard.slowestRecordAllTime.offsetMs)} on {new Date(leaderboard.slowestRecordAllTime.date).toLocaleDateString()}
                                    </Tooltip>
                                  </GoldTurtle>
                                )}
                                {isYearSlowest && leaderboard.slowestRecordYear && (
                                  <PurpleTurtle>
                                    <TurtleIcon size={14} />
                                    <Tooltip>
                                      Slowest AOTD {leaderboard.slowestRecordYear.year}: {formatOffsetMs(leaderboard.slowestRecordYear.offsetMs)} on {new Date(leaderboard.slowestRecordYear.date).toLocaleDateString()}
                                    </Tooltip>
                                  </PurpleTurtle>
                                )}
                              </BadgeSubGroup>
                            )}
                          </BadgeGroup>
                        )}
                        <FrameOwnerBadge info={ownerInfo} size="sm" />
                      </BadgeTab>
                    )}
                  </PostsCell>
                </tr>
              );
            })}
        </tbody>
      </table>
      <FrameLegend kinds={frameKinds} labels={FRAME_LABELS} />
      </FrameArea>
      </TableScrollWrap>
      {hasFrames && framesProvisional(frames) && <FrameNote>{PROVISIONAL_NOTE}</FrameNote>}
      {(leaderboard.fastestRecordAllTime || leaderboard.fastestRecordYear || leaderboard.slowestRecordAllTime || leaderboard.slowestRecordYear) && (
        <>
          <Legend>
            {leaderboard.fastestRecordAllTime && (
              <LegendItem>
                <FontAwesomeIcon icon={faBolt} size="xs" style={{ color: '#d4a017', filter: 'drop-shadow(0 0 3px #fbbf24) drop-shadow(0 0 6px #f59e0b80)' }} />
                <span>fastest ever</span>
              </LegendItem>
            )}
            {leaderboard.fastestRecordYear && (
              <LegendItem>
                <FontAwesomeIcon icon={faBolt} size="xs" style={{ color: '#7c3aed' }} />
                <span>fastest {leaderboard.fastestRecordYear.year}</span>
              </LegendItem>
            )}
            {leaderboard.slowestRecordAllTime && (
              <LegendItem>
                <span style={{ color: "#d4a017", filter: "drop-shadow(0 0 3px #fbbf24) drop-shadow(0 0 6px #f59e0b80)" }}><TurtleIcon size={10} /></span>
                <span>slowest ever</span>
              </LegendItem>
            )}
            {leaderboard.slowestRecordYear && (
              <LegendItem>
                <span style={{ color: "#7c3aed" }}><TurtleIcon size={10} /></span>
                <span>slowest {leaderboard.slowestRecordYear.year}</span>
              </LegendItem>
            )}
          </Legend>
        </>
      )}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"></link>
      <ShowMoreButton onClick={() => setShowAllAuthors(!showAllAuthors)}>
        {showAllAuthors ? <i className="fa fa-chevron-up"></i> : <i className="fa fa-chevron-down"></i>}
      </ShowMoreButton>
    </TableContainer>
    </>
  );
};

export default AotdAuthorsLeaderboard;
